import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { MotionBox, variants } from '../components/motion';

const VERIFY_ENDPOINT =
  process.env.REACT_APP_VERIFY_ENDPOINT || "";

const REDIRECT_URL =
  process.env.REACT_APP_REDIRECT_URL || "";

const MAIL_VERIFICATION_KEY =
  process.env.REACT_APP_MAIL_VERIFICATION_KEY || "";

export default function VerifyEmail() {
  const { keycloak } = useKeycloak();
  const email =
    (keycloak.idTokenParsed && keycloak.idTokenParsed.email) ||
    (keycloak.tokenParsed && keycloak.tokenParsed.email) ||
    '';

  const [state, setState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [err, setErr] = useState('');
  const didAutoSendRef = useRef(false);
  const sessionKey = email ? `verify-email:attempted:${email}` : '';

  const sendRequest = useCallback(async () => {
    if (!email) {
      setState('error');
      setErr('No email present in token.');
      return;
    }
    setState('sending');
    setErr('');

    try {
      const res = await fetch(VERIFY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Use the Keycloak bearer so the verify service can trust the caller.
          // Do NOT ship x-api-key secrets in the browser.
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: JSON.stringify({
          email,
          redirectUrl: REDIRECT_URL, // adapt to your backend field name
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`);
      }
      setState('sent');
    } catch (e) {
      setState('error');
      setErr(e?.message || 'Failed to send verification email.');
    }
  }, [email, keycloak.token]);

  useEffect(() => {
    // On first mount of this component in a session, send once per email.
    if (didAutoSendRef.current) return;
    didAutoSendRef.current = true;

    // If we already attempted this session, do not auto-send again.
    if (sessionKey && sessionStorage.getItem(sessionKey) === '1') {
      // Show the 'sent' UI with option to resend.
      setState('sent');
      return;
    }

    // Mark as attempted for this session before sending to avoid repeats on remount/refresh.
    if (sessionKey) sessionStorage.setItem(sessionKey, '1');
    sendRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  return (
    <Center minH="60vh">
      <MotionBox maxW="lg" w="100%" p={8} borderWidth="1px" borderRadius="lg" variants={variants.item} initial="hidden" animate="show">
        <VStack spacing={4} align="stretch" textAlign="center">
          <Heading size="lg">Verify your email</Heading>
          <Text>
            {email
              ? `We need to verify ${email} before you can continue.`
              : 'We need to verify your email before you can continue.'}
          </Text>

          {state === 'sending' && (
            <Center py={6}><Spinner size="lg" /></Center>
          )}

          {state === 'sent' && (
            <>
              <Text fontWeight="semibold">Verification email sent. Please check your inbox.</Text>
              <Text color="gray.600">
                After you click the link, you’ll be redirected to:
                <br />
                <code>{REDIRECT_URL}</code>
              </Text>
              <Button onClick={sendRequest} variant="outline">Resend email</Button>
              <Text fontSize="sm" color="gray.500">If you don’t see it, check spam/junk.</Text>
            </>
          )}

          {state === 'error' && (
            <>
              <Text color="red.500">Could not send verification email.</Text>
              <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap">{err}</Text>
              <Button onClick={sendRequest} colorScheme="blue">Try again</Button>
            </>
          )}

          {state === 'idle' && (
            <Button onClick={sendRequest} colorScheme="blue">Send verification email</Button>
          )}

          <Text fontSize="sm" color="gray.500">
            Wrong account?{' '}
            <Link color="blue.500" onClick={() => keycloak.logout()}>
              Log out
            </Link>
          </Text>
        </VStack>
      </MotionBox>
    </Center>
  );
}
